// Finance build (Workstream C — Amortization) + Financials tab redesign —
// read-only client for the loan Financials tab's schedule surfaces: the
// buyer's remaining P&I schedule, the seller schedule (note accrual vs actual
// seller distributions), and the redesign's broker + TPS schedules — all four
// projected from the existing servicing ledger's period spine. Each period's
// paymentId (when a payment was recorded) is the allocation-drawer drill-down key.

import { api } from "@/api/client";

export interface BuyerSchedulePeriodDto {
  periodIndex: number;
  dueDate: string;
  expectedInterest: number;
  expectedPrincipal: number;
  expectedTotal: number;
  principalBalanceAfter: number | null;
  isBalloon: boolean;
  status: string;
  paymentId: string | null;
  /** BLA Core §2.4 — the loan's rate of the scheduled P&I, charged on top. 0 on a loan
   *  without the fee. `expectedTotal` stays P&I alone; this plus that is what
   *  the borrower is actually debited. */
  paymentAdministrationFee: number;
  /** What the borrower's bank account is actually debited each month —
   *  `expectedTotal + paymentAdministrationFee`. Equals `expectedTotal` on a
   *  loan without the fee. */
  totalMonthlyDebit: number;
}

export interface SellerSchedulePeriodDto {
  periodIndex: number;
  dueDate: string;
  sellerExpectedInterest: number;
  sellerExpectedPrincipal: number;
  sellerExpectedTotal: number;
  sellerReceived: number;
  status: string;
  paymentId: string | null;
  /** The Buyer Consulting Fee carved out of this period (WF1 P10 monthly +
   *  P11 liquidity, summed) — actuals only, from the period payment's
   *  non-reversed disbursements. `sellerReceived` has ALREADY dropped by it;
   *  this is the line that names the amount. `sellerExpected*` stay GROSS.
   *
   *  Optional on the FE type so the inline test literals keep compiling (the
   *  LoanSummaryDto convention) — the server always sends it, 0 on a loan with
   *  no consulting fee. Read it as `?? 0`, never bare. */
  sellerConsultingDeduction?: number;
}

export interface BrokerSchedulePeriodDto {
  periodIndex: number;
  dueDate: string;
  brokerPaid: number;
  status: string;
  paymentId: string | null;
}

export interface TpsSchedulePeriodDto {
  periodIndex: number;
  dueDate: string;
  servicingFeeOnTop: number;
  tpsFeeCollected: number;
  distributionStatus: string | null;
  status: string;
  paymentId: string | null;
}

export interface LoanAmortizationDto {
  loanId: string;
  loanNumber: string;
  buyerSchedule: BuyerSchedulePeriodDto[];
  sellerSchedule: SellerSchedulePeriodDto[];
  brokerSchedule: BrokerSchedulePeriodDto[];
  tpsSchedule: TpsSchedulePeriodDto[];
}

export const amortizationApi = {
  get: (loanId: string) =>
    api.get<LoanAmortizationDto>(`/portfolio-admin/loans/${loanId}/amortization`),
};
